import { useRef, useState } from "react";
import { Target, Globe2, Truck } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { AU_VIEWBOX, AU_STATES, AU_PINS } from "../../data/auMap";

const POINTS = [
  { icon: Target, title: "South-East Australia — Primary Region", text: "Victoria, New South Wales and Tasmania are our core operating base for day-to-day delivery." },
  { icon: Globe2, title: "Australia-Wide Delivery When Required", text: "Available to mobilise for carrier and infrastructure projects anywhere in the country." },
  { icon: Truck, title: "Rapid Mobilisation Nationwide", text: "Crews and service vehicles ready to travel to regional, remote and interstate sites." },
];

const STATE_NOTES = {
  VIC: "Victoria — Head Office",
  NSW: "New South Wales — Primary Region",
  TAS: "Tasmania — Primary Region",
  ACT: "ACT — Primary Region",
};

export const Coverage = () => {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // { id, note, x, y }

  const onMove = (e, s) => {
    if (!s.highlight || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setHover({ id: s.id, note: STATE_NOTES[s.id] || s.name, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <section id="coverage" className="section bg-[#081C3A]" data-testid="coverage-section">
      <div className="container-custom">
        <SectionHeading
          eyebrow="Service Coverage"
          title="Rooted In South-East Australia, Available Nationwide"
          subtitle="Victoria, New South Wales and Tasmania are our primary operating region — with Australia-wide delivery available whenever a project requires it."
        />

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Map */}
          <Reveal>
            <div ref={wrapRef} className="relative rounded-2xl glass p-6 sm:p-10" data-testid="coverage-map-visual">
              <div className="absolute inset-0 hero-grid-overlay opacity-30 rounded-2xl" />
              <svg viewBox={AU_VIEWBOX} className="relative w-full h-auto" role="img" aria-label="Quanticom service coverage map — South-East Australia primary, nationwide available">
                {AU_STATES.map((s) => {
                  const active = hover?.id === s.id;
                  return (
                    <path
                      key={s.id}
                      d={s.d}
                      data-testid={`map-state-${s.id.toLowerCase()}`}
                      fill={s.highlight ? "#00D4FF" : "#0d2747"}
                      fillOpacity={s.highlight ? (active ? 1 : 0.92) : 1}
                      stroke={s.highlight ? "#7fe9ff" : "#23456e"}
                      strokeWidth={s.highlight ? (active ? 1.4 : 0.9) : 0.6}
                      onMouseEnter={(e) => onMove(e, s)}
                      onMouseMove={(e) => onMove(e, s)}
                      onMouseLeave={() => setHover(null)}
                      style={{
                        cursor: s.highlight ? "pointer" : "default",
                        transition: "fill-opacity 0.2s ease",
                        filter: s.highlight
                          ? `drop-shadow(0 0 ${active ? 7 : 4}px rgba(0,212,255,${active ? 0.95 : 0.65}))`
                          : undefined,
                      }}
                    >
                      <title>{STATE_NOTES[s.id] || s.name}</title>
                    </path>
                  );
                })}
                {AU_PINS.map((p) => {
                  const color = p.primary ? "#00D4FF" : "#D4A017";
                  return (
                    <g key={p.label}>
                      <circle cx={p.x} cy={p.y} r={p.primary ? 3.4 : 2.8} fill={color} stroke="#fff" strokeWidth="0.5" />
                      <circle cx={p.x} cy={p.y} r={p.primary ? 3.4 : 2.8} fill="none" stroke={color} strokeWidth="0.6">
                        <animate attributeName="r" from={p.primary ? "3.4" : "2.8"} to={p.primary ? "10" : "7"} dur="2.6s" repeatCount="indefinite" />
                        <animate attributeName="opacity" from="0.8" to="0" dur="2.6s" repeatCount="indefinite" />
                      </circle>
                    </g>
                  );
                })}
              </svg>

              {/* Hover tooltip */}
              {hover && (
                <div
                  className="pointer-events-none absolute z-10 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#04121f] bg-[#00D4FF] shadow-lg whitespace-nowrap -translate-x-1/2 -translate-y-full"
                  style={{ left: hover.x, top: hover.y - 10 }}
                  data-testid="map-tooltip"
                >
                  {hover.note}
                </div>
              )}

              {/* Legend */}
              <div className="relative flex items-center justify-center gap-6 mt-4 text-xs font-semibold" data-testid="coverage-legend">
                <span className="inline-flex items-center gap-2 text-[#00D4FF]">
                  <span className="w-3 h-3 rounded-full bg-[#00D4FF]" /> Primary Region
                </span>
                <span className="inline-flex items-center gap-2 text-[#D4A017]">
                  <span className="w-3 h-3 rounded-full bg-[#D4A017]" /> Nationwide Coverage
                </span>
              </div>
            </div>
          </Reveal>

          {/* Messaging */}
          <div>
            <Reveal>
              <p className="text-lg text-slate-300 leading-relaxed mb-8" data-testid="coverage-statement">
                Based in South-East Australia, Quanticom delivers day-to-day across Victoria, New South Wales and
                Tasmania — and is available to mobilise crews and deliver telecommunications infrastructure projects
                anywhere across Australia. Our team regularly travels to regional, remote and metropolitan locations
                to support carrier, infrastructure and network partners.
              </p>
            </Reveal>
            <div className="space-y-4">
              {POINTS.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.08}>
                  <div className="flex items-start gap-4 rounded-xl card-dark p-5" data-testid={`coverage-point-${i}`}>
                    <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-[#00D4FF]/10 border border-[#00D4FF]/25">
                      <p.icon className="w-5 h-5 text-[#00D4FF]" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white">{p.title}</h3>
                      <p className="text-sm text-slate-400 mt-0.5">{p.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
